<template>
  <section-page
    title="Leads and Deals"
    full-height
  >
    <app-loading-section
      :loading="!chartDataIsLoaded"
    >
      <div class="flex flex-between mt-150">
        <div class="chart_container">
          <h4 class="chart_container--heading">Leads</h4>
          <chart-bar
            :data-is-loaded="chartDataIsLoaded"
            :chart-data="leadChartData"
          ></chart-bar>
        </div>

        <div class="chart_container">
          <h4 class="chart_container--heading">Deals</h4>
          <chart-bar
            :data-is-loaded="chartDataIsLoaded"
            :chart-data="dealChartData"
            is-stacked
          ></chart-bar>
        </div>
      </div>

      <stats-section class="mt-200" has-columns>
        <stats-box
            label="Leads"
            subheading="last 30 days"
            info-text="Leads that were created in the last 30 days and have not been marked not a lead"
            class="column is-3"
            :request="getLeadsLast30Days"
        ></stats-box>

        <stats-box
            label="Deals"
            subheading="last 30 days"
            infoTip="Leads that have been marked as deals within the last 30 days"
            class="column is-3"
            :request="getDealsLast30Days"
        ></stats-box>

        <stats-box
            label="Deals from Follow Up"
            subheading="last 30 days"
            info-text="Leads marked as a deal in the last 30 days and have been in at least one follow up"
            class="column is-3"
            :request="getDealsFromFollowUpLast30Days"
        ></stats-box>

        <stats-box
            label="Deals from Follow Up"
            subheading="all time"
            info-text="All leads marked as a deal and have been in at least one follow up"
            class="column is-3"
            :request="getDealsFromFollowUpAllTime"
        ></stats-box>

        <stats-box
            label="Leads"
            subheading="all time"
            info-text="All leads that have not been marked not a lead"
            class="column is-3"
            :request="getLeadsAllTime"
        ></stats-box>

        <stats-box
            label="Deals"
            subheading="all time"
            info-text="All leads that have been marked as a deal"
            class="column is-3"
            :request="getDealAllTime"
        ></stats-box>

        <stats-box
            label="Lead to Deal Conversion"
            subheading="last 90 days"
            info-text="Number of leads with marked as deal divided by the total number of active leads created within last 90 days"
            class="column is-3"
            :request="getLeadsToDealConversionLast90Days"
        ></stats-box>

        <stats-box
            label="Lead to Deal Conversion"
            subheading="all time"
            info-text="Number of leads with marked as deal divided by the total number of active leads created"
            class="column is-3"
            :request="getLeadToDealConversionAllTime"
        ></stats-box>
      </stats-section>
    </app-loading-section>
  </section-page>
</template>

<script>
import StatsBox from "./components/StatsBox";
import api from "../../api";
import {StatsTimeFrames} from "../../services/staticData";
import StatsSection from "./components/StatsSection";
import dates from "../../services/dates";

export default {
  name: "StatsDealsTab",
  components: {StatsSection, StatsBox},
  data() {
    return {
      marketId: this.$store.getters.market.id,
      chartDataIsLoaded: false,
      leadChartData: {
        labels: dates.lastTwelveMonths(),
        datasets: [
          {
            label: 'All',
            data: [],
            backgroundColor: '#1C6C7D',
            minBarLength: 3,
          },
        ]
      },
      dealChartData: {
        labels: dates.lastTwelveMonths(),
        datasets: [
          {
            label: 'From Follow Up',
            data: [],
            backgroundColor: '#f6876e',
            minBarLength: 3,
          },
          {
            label: 'Not From Follow Up',
            data: [],
            backgroundColor: '#F25430',
            minBarLength: 3,
          },
        ]
      }
    }
  },
  created() {
    this.loadLeadAndDealStats();
  },
  methods: {
    loadLeadAndDealStats() {
      return Promise.all([
        api.stats.leadsPerMonthForLastYear(this.marketId),
        api.stats.dealsFromFollowUpPerMonthForLastYear(this.marketId),
        api.stats.dealsNotFromFollowUpPerMonthForLastYear(this.marketId),
      ]).then(([leads, dealsFromFollowUp, dealsNotFromFollowUp]) => {
        this.leadChartData.datasets[0].data = leads.data;

        this.dealChartData.datasets[0].data = dealsFromFollowUp.data;
        this.dealChartData.datasets[1].data = dealsNotFromFollowUp.data;

        this.chartDataIsLoaded = true;
      });
    },
    getLeadsLast30Days() {
      return api.stats.getQualifiedLeadsGeneratedStatBox(this.marketId, StatsTimeFrames["last-30-days"]);
    },
    getDealsLast30Days() {
      return api.stats.getDealsStatBox(this.marketId, StatsTimeFrames["last-30-days"]);
    },
    getDealsFromFollowUpLast30Days() {
      return api.stats.getDealsFromFollowUpStatBox(this.marketId, StatsTimeFrames["last-30-days"]);
    },
    getDealsFromFollowUpAllTime() {
      return api.stats.getDealsFromFollowUpStatBox(this.marketId, StatsTimeFrames["all-time"]);
    },
    getLeadsAllTime() {
      return api.stats.getQualifiedLeadsGeneratedStatBox(this.marketId, StatsTimeFrames["all-time"]);
    },
    getDealAllTime() {
      return api.stats.getDealsStatBox(this.marketId, StatsTimeFrames["all-time"]);
    },
    getLeadsToDealConversionLast90Days() {
      return api.stats.getLeadToDealConversionStatBox(this.marketId, StatsTimeFrames["last-90-days"]);
    },
    getLeadToDealConversionAllTime() {
      return api.stats.getLeadToDealConversionStatBox(this.marketId, StatsTimeFrames["all-time"]);
    },
  },
}
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.chart_container {
  width: 48%;
  position: relative;
  margin-bottom: 20px;

  &--heading {
    margin-bottom: -15px;
    font-weight: bold;
    font-size: 1.125rem;
  }
}

.stats-grid {
  display: flex;
  flex-wrap: wrap;
}

.stats-box {
  width: 25%;
  padding: 10px 0;

  &:nth-child(4n) {
    border-right: none;

    ::v-deep .stat_box--inner {
      border-right: none;
    }
  }

  &:nth-child(n + 5) {
    border-top: 1px solid $border;
  }

  ::v-deep .stat_box--inner {
    margin: 10px 0;
    padding: 8px 20px;
    border-right: 1px solid $border;
  }
}

</style>
